import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Button, Card, Col, Form, FormGroup, Input, InputGroup, InputGroupAddon, Row, Table, Badge } from 'reactstrap'
import toastr from 'toastr'
import moment from 'moment'

import { fetchContadores, getServerMessage, invitarContador, removerContador, reenviarInvitacion } from '../../helpers/empresaUsuarios'

const INVITE_EXPIRY_DAYS = 14

const isInviteExpired = (invitedAt) => {
    if (!invitedAt) return false
    return Date.now() - new Date(invitedAt).getTime() > INVITE_EXPIRY_DAYS * 24 * 60 * 60 * 1000
}

const Contadores = ({ empresaId }) => {
    const [contadores, setContadores] = useState([])
    const [email, setEmail] = useState('')
    const [nombre, setNombre] = useState('')
    const [showInviteForm, setShowInviteForm] = useState(false)

    const loadContadores = async () => {
        if (!empresaId) {
            return
        }

        try {
            const data = await fetchContadores(empresaId)
            setContadores(data)
        } catch (error) {
            toastr.error(getServerMessage(error, 'No pude cargar los contadores de esta empresa.'))
        }
    }

    useEffect(() => {
        loadContadores()
    }, [empresaId])

    const _invite = async (event) => {
        event.preventDefault()

        if (!email) {
            toastr.error('Escribe el correo del contador que quieres invitar.')
            return
        }

        if (!nombre) {
            toastr.error('Escribe el nombre del contador que quieres invitar.')
            return
        }

        try {
            const response = await invitarContador(empresaId, { email, nombre })

            if (!response.data?.success) {
                toastr.error(response.data?.message || 'No pude enviar la invitación.')
                return
            }

            toastr.success('Invitación enviada correctamente.')
            setEmail('')
            setNombre('')
            setShowInviteForm(false)
            await loadContadores()
        } catch (error) {
            toastr.error(getServerMessage(error, 'No pude enviar la invitación.'))
        }
    }

    const _remove = async (userId) => {
        if (!window.confirm('¿Seguro que deseas remover a este contador?')) {
            return
        }

        try {
            const response = await removerContador(empresaId, userId)

            if (!response.data?.success) {
                toastr.error(response.data?.message || 'No pude remover al contador.')
                return
            }

            toastr.success('Contador removido correctamente.')
            await loadContadores()
        } catch (error) {
            toastr.error(getServerMessage(error, 'No pude remover al contador.'))
        }
    }

    const _resend = async (userId) => {
        try {
            const response = await reenviarInvitacion(empresaId, userId)

            if (!response.data?.success) {
                toastr.error(response.data?.message || 'No pude reenviar la invitación.')
                return
            }

            toastr.success('Invitación reenviada.')
            setContadores(prev => prev.map(c =>
                c.userId === userId ? { ...c, invitedAt: new Date().toISOString() } : c
            ))
        } catch (error) {
            toastr.error(getServerMessage(error, 'No pude reenviar la invitación.'))
        }
    }

    return (
        <Card body>
            <Row className="mb-3">
                <Col md={8}>
                    <h3>Contadores</h3>
                    <p>Invita contadores a administrar esta empresa y revisa cuáles siguen pendientes.</p>
                </Col>
                <Col md={4} className="text-md-right">
                    <Button color="primary" onClick={() => setShowInviteForm(!showInviteForm)}>
                        Invitar contador
                    </Button>
                </Col>
            </Row>

            {showInviteForm && (
                <Form onSubmit={_invite}>
                    <Row className="mb-4">
                        <Col md={6}>
                            <FormGroup>
                                <Input
                                    type="text"
                                    value={nombre}
                                    placeholder="Nombre del contador"
                                    onChange={(event) => setNombre(event.target.value)}
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <InputGroup>
                                    <Input
                                        type="email"
                                        value={email}
                                        placeholder="correo@ejemplo.com"
                                        onChange={(event) => setEmail(event.target.value)}
                                        required
                                    />
                                    <InputGroupAddon addonType="append">
                                        <Button type="submit" color="success">Enviar invitación</Button>
                                    </InputGroupAddon>
                                </InputGroup>
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
            )}

            <Table responsive>
                <thead>
                    <tr>
                        <th>Nombre</th>
                        <th>Email</th>
                        <th>Fecha de ingreso</th>
                        <th>Status</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {contadores.map((contador) => {
                        const expired = contador.status === 'pending' && isInviteExpired(contador.invitedAt)

                        return (
                            <tr key={contador._id}>
                                <td>{contador.nombre || 'Sin nombre'}</td>
                                <td>{contador.email}</td>
                                <td>
                                    {contador.status === 'pending'
                                        ? contador.invitedAt
                                            ? `Invitado el ${moment(contador.invitedAt).format('DD/MM/YYYY')}`
                                            : 'Pendiente'
                                        : contador.acceptedAt
                                            ? moment(contador.acceptedAt).format('DD/MM/YYYY')
                                            : '—'}
                                </td>
                                <td>
                                    {contador.status === 'pending' ? (
                                        <>
                                            <Badge color="warning" className="mr-1">Pendiente</Badge>
                                            {expired && <Badge color="danger">Expirada</Badge>}
                                        </>
                                    ) : (
                                        <Badge color="success">Activo</Badge>
                                    )}
                                </td>
                                <td>
                                    {contador.status === 'pending' && contador.userId && (
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            className="mr-1"
                                            onClick={() => _resend(contador.userId)}
                                        >
                                            Reenviar invitación
                                        </Button>
                                    )}
                                    {contador.userId && (
                                        <Button color="danger" size="sm" onClick={() => _remove(contador.userId)}>
                                            Remover
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                    {contadores.length === 0 && (
                        <tr>
                            <td colSpan={5}>Aún no hay contadores vinculados a esta empresa.</td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </Card>
    )
}

const mapStateToProps = (state) => ({
    empresaId: state.Auth.profile?.empresa?._id || ''
})

export default connect(mapStateToProps)(Contadores)

